// Recortar y Marca de agua SOBRE el lienzo — ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// Las dos necesitan un canvas grande además de sus controles, así que no entran en una
// hoja: se abren como una capa sobre la columna del lienzo, con el canvas a la izquierda
// y la tarjeta de controles a la derecha, la misma anatomía que el visor de escritorio.
// El hook de cada una (`useCropState`, `useWatermarkEditor`) tiene que vivir en el
// componente que monta los dos slots: por eso cada herramienta es un componente aparte.
//
// Recortar termina en una descarga (no guarda imagen) y Marca de agua guarda la
// configuración de la marca (no toca la imagen): ninguna cuelga nada del lienzo.

import { useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { ImageCropCanvas, ImageCropControls, useCropState } from '../../../components/ImageCropModal';
import { useWatermarkEditor } from '../../../components/watermark/useWatermarkEditor';
import { WatermarkEditorCanvas } from '../../../components/watermark/WatermarkEditorCanvas';
import { WatermarkEditorControls } from '../../../components/watermark/WatermarkEditorControls';
import type { CeldaParaAccion } from '../accionesDeCelda';
import { useUrlDelOriginal } from './useUrlDelOriginal';

const Capa: React.FC<{ titulo: string; onCerrar: () => void; anchoControles: number; canvas: React.ReactNode; controles: React.ReactNode }> = ({
  titulo,
  onCerrar,
  anchoControles,
  canvas,
  controles,
}) => {
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [onCerrar]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      className="absolute inset-0 z-20 flex items-stretch gap-4 bg-[#F3F4F8]/95 p-6 backdrop-blur-[2px]"
    >
      <div className="relative min-w-0 flex-1 overflow-hidden rounded-2xl bg-[#E9EAF0]">
        <div className="absolute inset-0">{canvas}</div>
      </div>
      <div
        className="relative flex shrink-0 flex-col overflow-hidden rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.06)] bg-white shadow-[0_6px_20px_rgb(var(--brand-black-rgb)_/_0.06)]"
        style={{ width: anchoControles }}
      >
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 transition-colors hover:bg-black/[0.1]"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="min-h-0 flex-1 overflow-y-auto">{controles}</div>
      </div>
    </div>
  );
};

const Esperando: React.FC = () => (
  <div className="flex h-full w-full items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-brand-primary" aria-hidden="true" />
  </div>
);

export const RecorteSobreElLienzo: React.FC<{ celda: CeldaParaAccion; onCerrar: () => void }> = ({
  celda,
  onCerrar,
}) => {
  const url = useUrlDelOriginal(celda);
  const cropState = useCropState(url ?? '', celda.datos.fileName ?? '', onCerrar);
  return (
    <Capa
      titulo="Recortar"
      onCerrar={onCerrar}
      anchoControles={300}
      canvas={url ? <ImageCropCanvas cropState={cropState} /> : <Esperando />}
      controles={<ImageCropControls cropState={cropState} onCancel={onCerrar} variant="desktop" />}
    />
  );
};

export const MarcaDeAguaSobreElLienzo: React.FC<{ celda: CeldaParaAccion; onCerrar: () => void }> = ({
  celda,
  onCerrar,
}) => {
  const url = useUrlDelOriginal(celda);
  const state = useWatermarkEditor({ active: !!url, imageUrl: url, onClose: onCerrar });
  return (
    <Capa
      titulo="Marca de agua"
      onCerrar={onCerrar}
      anchoControles={400}
      canvas={url ? <WatermarkEditorCanvas state={state} /> : <Esperando />}
      controles={<WatermarkEditorControls state={state} variant="desktop" />}
    />
  );
};
